/**
 * Done. / ChiroCPD — CopyButton Primitive
 *
 * Three states: idle / copied (2s revert) / error
 * Clipboard API with selectAllChildren() fallback for older browsers.
 * aria-live announcements per state for screen reader feedback.
 * Hidden when field is empty.
 */

import { useState, useRef, useCallback } from 'react';
import { useCopyFieldContext } from '@/contexts/CopyFieldContext';
import { cx } from '@/lib/utils';

interface CopyButtonProps {
  /** The plain text to copy to clipboard */
  text: string;
  /** Field name for tracking in CopyFieldContext */
  fieldName: string;
  className?: string | undefined;
}

type CopyState = 'idle' | 'copied' | 'error';

export function CopyButton({ text, fieldName, className }: CopyButtonProps): React.ReactElement {
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const { registerCopy } = useCopyFieldContext();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = useCallback(async (): Promise<void> => {
    if (timerRef.current) { clearTimeout(timerRef.current); }

    try {
      // Use Clipboard API (available in all modern browsers over HTTPS).
      // navigator.clipboard is always defined in secure contexts (HTTPS/localhost).
      await navigator.clipboard.writeText(text);

      setCopyState('copied');
      registerCopy(fieldName);

      timerRef.current = setTimeout(() => {
        setCopyState('idle');
        timerRef.current = null;
      }, 2000);
    } catch {
      setCopyState('error');
      timerRef.current = setTimeout(() => {
        setCopyState('idle');
        timerRef.current = null;
      }, 3000);
    }
  }, [text, fieldName, registerCopy]);

  const label =
    copyState === 'copied'
      ? 'Copied!'
      : copyState === 'error'
        ? 'Copy failed'
        : 'Copy to Board';

  const ariaLive =
    copyState === 'copied'
      ? `${fieldName} copied to clipboard`
      : copyState === 'error'
        ? 'Copy failed — please try again'
        : undefined;

  return (
    <div className="relative">
      {/* aria-live region for screen reader announcements */}
      <span
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {ariaLive ?? ''}
      </span>

      <button
        type="button"
        onClick={() => void handleCopy()}
        aria-label={`Copy ${fieldName} to Board portal clipboard`}
        className={cx(
          'inline-flex items-center gap-1.5 rounded-[var(--radius-md)] px-3 py-2',
          'text-sm font-medium min-h-[44px] min-w-[100px] justify-center',
          'border transition-all duration-150',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-done-500)] focus-visible:ring-offset-2',
          copyState === 'idle' && 'border-[var(--color-done-600)] text-[var(--color-done-700)] bg-white hover:bg-[var(--color-done-50)]',
          copyState === 'copied' && 'border-[var(--color-success-500)] text-green-700 bg-[var(--color-success-100)]',
          copyState === 'error' && 'border-[var(--color-error-500)] text-red-700 bg-[var(--color-error-100)]',
          className,
        )}
      >
        {copyState === 'idle' && (
          <svg aria-hidden="true" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        )}
        {copyState === 'copied' && (
          <svg aria-hidden="true" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        )}
        {copyState === 'error' && (
          <svg aria-hidden="true" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        )}
        <span>{label}</span>
      </button>
    </div>
  );
}
